import React, { useEffect, useRef, useState } from 'react'
import { DatePicker } from '@material-ui/pickers'
import { Select, MenuItem } from '@material-ui/core'

import moment from 'config/moment'
import { snackbar } from 'components'

import ManualCampaigns from './ManualCampaigns/ManualCampaigns'
import PushNotifications from './PushNotifications/PushNotifications'
import AutomaticCampaigns from './AutomaticCampaigns/AutomaticCampaigns'
import { useDashboardFilters } from '../contexts/DashboardFiltersContext'
import { montserratLightStyle, montserratStyle } from '../styles'

const periodOptions = { 12: 'Últimos 30 días', 13: 'Últimos 7 días' }

const getPreviousMonths = () => {
  const months: { [key: number]: string } = {}
  const now = moment()

  for (let i = 0; i < 6; i += 1) {
    const monthDate = now.clone().subtract(i, 'months')
    const monthIndex = monthDate.month()
    const key = i * 100 + monthIndex
    const monthName = monthDate.format('MMMM YYYY')
    months[key] = monthName.charAt(0).toUpperCase() + monthName.slice(1)
  }
  return months
}

const getDefaultPeriodValue = () => {
  const now = moment()
  return 0 * 100 + now.month()
}

const MarketingTab = () => {
  const {
    filters,
    handleStartPeriod1Change,
    handleEndPeriod1Change,
    handlePeriodSelect,
    handleApply,
    isActiveTab,
  } = useDashboardFilters()
  const { period, startPeriod1, endPeriod1, applyTrigger, isLoading } = filters

  const previousMonths = getPreviousMonths()
  const defaultPeriodValue = React.useMemo(() => getDefaultPeriodValue(), [])
  const [periodSelectValue, setPeriodSelectValue] = useState<number>(defaultPeriodValue)

  const shouldTrigger = isActiveTab('marketing')

  const [step, setStep] = useState(0)
  const runRef = useRef(0)

  useEffect(() => {
    if (!shouldTrigger) return
    runRef.current = applyTrigger
    setStep(0)
  }, [applyTrigger, shouldTrigger])

  const onApply = () => {
    if (startPeriod1.isAfter(endPeriod1)) {
      snackbar.show('La fecha de inicio debe ser anterior a la fecha de fin')
      return
    }
    // Sync reset so Push gets trigger on the same apply cycle (avoid one frame of stale data)
    runRef.current = applyTrigger + 1
    setStep(0)
    handleApply()
  }

  const resetTrigger = shouldTrigger ? applyTrigger : 0

  return (
    <div className="flex flex-col gap-8 p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-row items-end gap-4 w-full">
        <div style={{ width: '180px' }}>
          <Select
            value={periodSelectValue}
            onChange={e => {
              const value = e.target.value as number
              setPeriodSelectValue(value)
              if (value >= 0) {
                handlePeriodSelect(value)
              }
            }}
            fullWidth
            style={{ ...montserratStyle }}
          >
            <MenuItem value={-1}>Seleccionar período</MenuItem>
            {Object.entries(previousMonths).map(([monthIndex, monthName]) => (
              <MenuItem key={monthIndex} value={parseInt(monthIndex, 10)}>
                {monthName}
              </MenuItem>
            ))}
            {Object.entries(periodOptions).map(([key, label]) => (
              <MenuItem key={key} value={parseInt(key, 10)}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="marketing-start-period"
            className="block text-sm text-gray-500 mb-1"
            style={{ ...montserratLightStyle }}
          >
            Desde
          </label>
          <DatePicker
            value={startPeriod1}
            onChange={date => {
              handleStartPeriod1Change(date)
              setPeriodSelectValue(-1)
            }}
            format="DD/MM/YYYY"
            InputProps={{
              id: 'marketing-start-period',
              disableUnderline: false,
              style: { fontSize: 18, color: '#6B7280', paddingLeft: 8, ...montserratStyle },
            }}
            style={{ width: '150px', borderRadius: 8 }}
            maxDate={endPeriod1}
          />
        </div>

        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="marketing-end-period"
            className="block text-sm text-gray-500 mb-1"
            style={{ ...montserratLightStyle }}
          >
            Hasta
          </label>
          <DatePicker
            value={endPeriod1}
            onChange={date => {
              handleEndPeriod1Change(date)
              setPeriodSelectValue(-1)
            }}
            format="DD/MM/YYYY"
            InputProps={{
              id: 'marketing-end-period',
              disableUnderline: false,
              style: { fontSize: 18, color: '#6B7280', paddingLeft: 8, ...montserratStyle },
            }}
            style={{ width: '150px', borderRadius: 8 }}
            minDate={startPeriod1}
            maxDate={moment()}
          />
        </div>

        <button
          type="button"
          className="ml-auto px-6 py-2 rounded bg-[#26342E] text-white font-semibold hover:bg-[#1a2420] transition disabled:opacity-50"
          style={{ ...montserratStyle }}
          onClick={onApply}
          disabled={isLoading}
        >
          {isLoading ? 'Cargando...' : 'Aplicar'}
        </button>
      </div>
      <PushNotifications
        period={period}
        startPeriod1={startPeriod1}
        endPeriod1={endPeriod1}
        trigger={shouldTrigger && step === 0 ? applyTrigger : 0}
        resetTrigger={resetTrigger}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 0 ? 1 : prev))
        }}
      />
      <ManualCampaigns
        period={period}
        startPeriod1={startPeriod1}
        endPeriod1={endPeriod1}
        trigger={shouldTrigger && step === 1 ? applyTrigger : 0}
        resetTrigger={resetTrigger}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 1 ? 2 : prev))
        }}
      />
      <AutomaticCampaigns
        period={period}
        startPeriod1={startPeriod1}
        endPeriod1={endPeriod1}
        trigger={shouldTrigger && step === 2 ? applyTrigger : 0}
        resetTrigger={resetTrigger}
        onLoaded={() => {
          if (runRef.current !== applyTrigger) return
          setStep(prev => (prev === 2 ? 3 : prev))
        }}
      />
    </div>
  )
}

export default MarketingTab
